/**
 * `useSettingDefinitions` — module-scoped cached fetch of the
 * server-side `/settings/definitions` registry.
 *
 * One fetch per client session, shared by every consumer. No provider
 * component required: just call the composable.
 */

import { ref } from 'vue'
import { isAgencySettingsCode } from '@gat-ship/types'
import type {
  AgencySettingName,
  GetSettingDefinitionResponse,
  SettingDefinition,
  SettingName
} from '@gat-ship/types'
import { useSettingsApiCalls } from './useSettingsApiCalls'

// Module-level cache: one source of truth across all callers.
const definitions = ref<GetSettingDefinitionResponse | undefined>(undefined)
const isLoading = ref(false)
let inFlight: Promise<GetSettingDefinitionResponse> | undefined

async function fetchOnce(): Promise<GetSettingDefinitionResponse> {
  if (definitions.value) return definitions.value
  if (inFlight) return inFlight
  const { getSettingDefinitionsRequest } = useSettingsApiCalls()
  isLoading.value = true
  inFlight = getSettingDefinitionsRequest()
    .then((res) => {
      definitions.value = res
      return res
    })
    .finally(() => {
      isLoading.value = false
      inFlight = undefined
    })
  return inFlight
}

export function useSettingDefinitions() {
  /** Synchronous lookup — returns undefined before the fetch lands. */
  function get(
    settingName: SettingName
  ): SettingDefinition<SettingName> | undefined {
    if (!definitions.value) return undefined
    if (isAgencySettingsCode(settingName)) {
      return definitions.value.agencyLevelDefinitions[
        settingName
      ] as SettingDefinition<AgencySettingName>
    }
    return undefined
  }

  /** Awaitable lookup — kicks off the fetch on first call. */
  async function getAsync(
    settingName: SettingName
  ): Promise<SettingDefinition<SettingName>> {
    const res = await fetchOnce()
    if (isAgencySettingsCode(settingName)) {
      return res.agencyLevelDefinitions[
        settingName
      ] as SettingDefinition<AgencySettingName>
    }
    throw new Error(`Unknown setting: ${settingName}`)
  }

  /** Trigger the fetch in the background — e.g. from a layout mount. */
  function prefetch() {
    void fetchOnce()
  }

  return {
    definitions,
    isLoading,
    get,
    getAsync,
    prefetch
  }
}
